import { useChatStore } from '@/store/useChatStore';
import { useEffect, useRef } from 'react';

/**
 * Hook to track when any input/textarea is focused on the page.
 * This is useful for detecting when the mobile keyboard is open.
 * Should be used once at the App level.
 *
 * Uses both focus events AND Visual Viewport API to detect keyboard visibility,
 * because on mobile devices the input can remain focused even after the keyboard is hidden.
 */
export function useInputFocusTracker() {
  const setInputFocused = useChatStore((state) => state.setInputFocused);
  const isFocusedRef = useRef(false);
  const initialViewportHeight = useRef<number | null>(null);
  const keyboardOpenRef = useRef(false);

  useEffect(() => {
    // Store initial viewport height on mount
    if (window.visualViewport) {
      initialViewportHeight.current = window.visualViewport.height;
    }

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        isFocusedRef.current = true;
        setInputFocused(true);
      }
    };

    const handleFocusOut = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        isFocusedRef.current = false;
        setInputFocused(false);
      }
    };

    // Visual Viewport resize handler to detect keyboard hide on mobile
    // When keyboard hides, the visual viewport height increases back to normal
    const handleViewportResize = () => {
      if (!window.visualViewport || initialViewportHeight.current === null)
        return;

      const currentHeight = window.visualViewport.height;
      const heightDiff = initialViewportHeight.current - currentHeight;

      // Never blur the active element here: on Android (Samsung keyboard,
      // autofill/password manager bars) transient resizes right after focus
      // would close the keyboard before the user can type. We only sync state.
      if (heightDiff > 150) {
        keyboardOpenRef.current = true;
        // Keyboard reopened on an input that kept focus (no new focusin event)
        if (isFocusedRef.current) {
          setInputFocused(true);
        }
      } else if (heightDiff < 100 && keyboardOpenRef.current) {
        keyboardOpenRef.current = false;
        if (isFocusedRef.current) {
          // Keyboard is hidden but input is still focused - update state
          setInputFocused(false);
        }
      }

      // Update initial height when viewport is fully visible (handles orientation changes)
      if (heightDiff < 50) {
        initialViewportHeight.current = currentHeight;
      }
    };

    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);

    // Add Visual Viewport listener for keyboard detection
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportResize);
    }

    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener(
          'resize',
          handleViewportResize
        );
      }
    };
  }, [setInputFocused]);
}
